import React, { useEffect, useId, useRef } from "react";
import { cn } from "../../utils/cn";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
}

export const BaseModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  title,
  className,
  overlayClassName,
  contentClassName,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && closeOnEscape) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose, closeOnEscape]);

  const handleOverlayClick = (event: React.MouseEvent) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  const id = useId();

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "fixed inset-0 z-[101] flex items-center justify-center font-inter px-1.5",
        "bg-black/80 backdrop-blur-xl",
        "animate-in fade-in duration-200",
        overlayClassName
      )}
      onClick={handleOverlayClick}>
      <div
        ref={modalRef}
        className={cn(
          "relative bg-card rounded-3xl isolate",
          "max-w-md w-full max-h-[90vh] overflow-hidden",
          "animate-in zoom-in-95 duration-200 px-2 py-2",
          className
        )}
        onClick={(e) => e.stopPropagation()}>
        <svg
          viewBox="0 0 1024 1024"
          aria-hidden="true"
          className="absolute left-1/2 top-1/2 -z-10 size-[64rem] -translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)] sm:left-full sm:-ml-80 lg:left-1/2 lg:ml-0 lg:-translate-x-1/2 lg:translate-y-0">
          <circle
            r={512}
            cx={512}
            cy={512}
            fill={`url(#${id})`}
            fillOpacity="0.7"
          />
          <defs>
            <radialGradient id={id}>
              <stop stopColor="#ff6b42" />
              <stop offset={1} stopColor="#ff6b42" />
            </radialGradient>
          </defs>
        </svg>

        <div className="rounded-2xl bg-black/75">
          {/* Header */}
          {(title || showCloseButton) && (
            <div className="flex items-center justify-between p-4">
              {title && (
                <h2 className="text-lg font-medium text-white">{title}</h2>
              )}
              {showCloseButton && (
                <button
                  onClick={onClose}
                  className={cn(
                    "p-1 rounded-md hover:bg-white/10",
                    "text-white/50 hover:text-white",
                    "transition-colors duration-200"
                  )}
                  aria-label="Close modal">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Content */}
          <div className={cn("px-4 pb-4", contentClassName)}>{children}</div>
        </div>
      </div>
    </div>
  );
};
